/**
 * Raising a ticket on a client's behalf.
 *
 * The staff version of the client portal's form, and the difference is the
 * point: here the client is chosen, and an assignee and a project can be set.
 * A client's own form has none of those — see the portal's `raise-ticket-dialog`
 * for why.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  RecordSelect,
  Select,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

const schema = z.object({
  subject: z.string().trim().min(1, 'Give the ticket a subject.').max(255),
  body: z.string().trim().min(1, 'Say what the problem is.').max(20_000),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = { subject: '', body: '', priority: 'normal' };

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function RaiseTicketDialog({
  open,
  onClose,
  onRaised,
  clientPublicId,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  onRaised?: (publicId: string) => void;
  clientPublicId?: string;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [client, setClient] = useState<string | null>(clientPublicId ?? null);
  const [project, setProject] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULTS);
    setClient(clientPublicId ?? null);
    setProject(null);
    setAssignee(null);
  }, [open, form, clientPublicId]);

  const raise = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ public_id: string; code: string }>(
        `/support/tickets/for-client/${client}`,
        { ...values, project_id: project, assignee_id: assignee },
      ),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['support'] });
      toast.success('Ticket raised', record.code);
      onRaised?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (client === null) {
      form.setError('root', { message: 'Choose the client this is for.' });
      return;
    }
    void form.handleSubmit((values) => raise.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New ticket"
      description="Raised on a client's behalf. They will see it in their portal."
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={raise.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={raise.isPending} onClick={submit}>
            Raise ticket
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <RecordSelect
          label="Client"
          required
          endpoint="/crm/clients"
          value={client}
          valueLabel={clientName}
          onChange={(next) => {
            setClient(next);
            // The project belonged to the previous client. Keeping it would
            // send the server a project from one client under another, which
            // it refuses — better to clear it than to be refused.
            setProject(null);
          }}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.code ?? ''),
          })}
          placeholder="Search clients…"
        />

        <Input
          label="Subject"
          required
          error={form.formState.errors.subject?.message}
          {...form.register('subject')}
        />
        <Textarea
          label="What is happening?"
          required
          rows={5}
          hint="This is visible to the client. Internal notes go on the ticket afterwards."
          error={form.formState.errors.body?.message}
          {...form.register('body')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Priority"
            options={PRIORITY_OPTIONS}
            error={form.formState.errors.priority?.message}
            {...form.register('priority')}
          />
          <RecordSelect
            label="Assignee"
            endpoint="/hr/employees"
            value={assignee}
            onChange={(next) => setAssignee(next)}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.full_name),
              detail: String(row.job_title ?? ''),
            })}
            placeholder="Leave to the queue"
          />
          <RecordSelect
            label="Project"
            className="sm:col-span-2"
            endpoint="/projects"
            value={project}
            onChange={(next) => setProject(next)}
            params={client ? { client } : undefined}
            toOption={(row) => ({
              value: String(row.public_id),
              label: String(row.name),
              detail: String(row.client_name ?? ''),
            })}
            placeholder="Not about a specific project"
            disabled={client === null}
          />
        </div>
      </form>
    </Modal>
  );
}
